import { useNavigate, useSearchParams } from "react-router";
import { getRuntimeConfig } from "@shared-core/config/runtime-config";
import { useSupplierPortalSignIn } from "@shared-core/data/SupplierPortalAdapterContext";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  ACTION_WORDING,
  signInErrorSentence,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { SupplierHeader } from "../shell/SupplierHeader";

/** Only a path in this app may be returned to: never another origin. */
function safeReturnTo(raw: string | null): string {
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
}

/**
 * `/sign-in`: the login way in (phase 3). Staff add supplier people, so there is no sign-up; anyone without a login
 * uses the link in the order email. The real sign-in hands over to the supplier app's Entra External ID flow
 * (through `SupplierPortalSignInAdapter`); the mock signs the fixture person in.
 *
 * In a testing preview (`supplierAuthMode: "simulated"`) there is no External ID to sign in with: a tester signs in as
 * a supplier person from the test console, so the page says that instead. `supplierAuthMode` is the runtime
 * configuration's unless a story passes one.
 */
export function SignInPage({
  supplierAuthMode,
}: { supplierAuthMode?: string } = {}) {
  const mode = supplierAuthMode ?? getRuntimeConfig().supplierAuthMode;
  return mode === "simulated" ? <SimulatedSignIn /> : <ExternalIdSignIn />;
}

/** A testing preview: sign in from the test console (its "Sign in as" link opens this portal signed in). */
function SimulatedSignIn() {
  return (
    <div className="min-h-screen bg-muted/30">
      <SupplierHeader right={null} />
      <main className="mx-auto max-w-md px-4 py-10">
        <Card className="gap-4 p-6">
          <h1 className="text-lg font-semibold">
            Sign in from the test console
          </h1>
          <p className="text-sm text-muted-foreground">
            This is a testing preview. Open the test console in the staff app,
            go to Suppliers and choose “Sign in as” beside a supplier user. The
            link signs this browser in once, within a minute.
          </p>
          <p className="text-sm text-muted-foreground">
            To see one order as its supplier without signing in, choose “Open as
            supplier” on the order instead.
          </p>
        </Card>
      </main>
    </div>
  );
}

function ExternalIdSignIn() {
  const signIn = useSupplierPortalSignIn();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("return_to"));
  // The supplier app sends the browser back here with `?error=<reason>` when sign-in did not complete.
  const refusal = signInErrorSentence(params.get("error"));
  const write = useFieldErrorWrite(
    "We couldn't sign you in. Please try again.",
  );

  return (
    <div className="min-h-screen bg-muted/30">
      <SupplierHeader right={null} />
      <main className="mx-auto max-w-md px-4 py-10">
        <Card className="gap-4 p-6">
          <h1 className="text-lg font-semibold">
            Sign in to the supplier portal
          </h1>
          <p className="text-sm text-muted-foreground">
            See all your Pollards orders and invoices in one place. Pollards
            purchasing sets up your login; if you don&apos;t have one, use the
            link in the order email, or ask your Pollards contact.
          </p>
          {refusal && (
            <p role="alert" className="text-sm text-red-600">
              {refusal}
            </p>
          )}
          {write.banner && (
            <p role="alert" className="text-sm text-red-600">
              {write.banner}
            </p>
          )}
          <Button
            disabled={write.isBusy}
            onClick={() =>
              void write
                .run(() => signIn.signIn(returnTo))
                .then(async (ok) => {
                  // The mock signs in on the spot; the real adapter has handed the browser to the identity
                  // provider, so there is no session to go on with.
                  if (ok && (await signIn.getSession()))
                    navigate(returnTo, { replace: true });
                })
            }
          >
            {ACTION_WORDING.signIn}
          </Button>
        </Card>
      </main>
    </div>
  );
}
